import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('mandispot_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export const auth = {
  sendOtp: (phone, email) => api.post('/auth/otp/send', { phone, email }).then(r => r.data),
  verifyOtp: (data) => api.post('/auth/otp/verify', data).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  updateMe: (data) => api.patch('/auth/me', data).then(r => r.data),
  securityAlerts: () => api.get('/auth/security/alerts').then(r => r.data),
  ackSecurityAlert: (id, wasntMe = false) =>
    api.post(`/auth/security/alerts/${id}/ack`, null, { params: { wasnt_me: wasntMe } })
      .then(r => r.data),
};

export const stores = {
  list: (params) => api.get('/stores', { params }).then(r => r.data),
  get: (id) => api.get(`/stores/${id}`).then(r => r.data),
  create: (data) => api.post('/stores', data).then(r => r.data),
  myStores: () => api.get('/owner/stores').then(r => r.data),
  byPincode: (pincode) => api.get(`/stores/by-pincode/${pincode}`).then(r => r.data),
};

export const pincodes = {
  served: () => api.get('/pincodes').then(r => r.data),
};

export const products = {
  add: (storeId, data) => api.post(`/stores/${storeId}/products`, data).then(r => r.data),
  remove: (id) => api.delete(`/products/${id}`).then(r => r.data),
  update: (id, data) => api.patch(`/products/${id}`, data).then(r => r.data),
  priceHistory: (id, days = 90) => api.get(`/products/${id}/price-history`, { params: { days } }).then(r => r.data),
  similar: (id, limit = 8) => api.get(`/products/${id}/similar`, { params: { limit } }).then(r => r.data),
  nearbyAlternatives: (id, params = {}) =>
    api.get(`/products/${id}/nearby-alternatives`, { params }).then(r => r.data),
};

export const orders = {
  create: (data) => api.post('/orders', data).then(r => r.data),
  mine: () => api.get('/orders/me').then(r => r.data),
  forOwner: () => api.get('/orders/owner').then(r => r.data),
  get: (id) => api.get(`/orders/${id}`).then(r => r.data),
  track: (id) => api.get(`/orders/${id}/track`).then(r => r.data),
  updateStatus: (id, status, delivery_otp) => api.patch(`/orders/${id}/status`, { status, delivery_otp }).then(r => r.data),
  verifyOtp: (id, code) => api.post(`/orders/${id}/verify-otp`, { code }).then(r => r.data),
  returnRequest: (id, data) => api.post(`/orders/${id}/return-request`, data).then(r => r.data),
  lastBasket: () => api.get('/orders/last-basket').then(r => r.data),
};

export const promo = {
  firstOrder: (subtotal) => api.get('/promo/first-order', { params: { subtotal } }).then(r => r.data),
};

export const coins = {
  me: () => api.get('/coins/me').then(r => r.data),
  redeemPreview: (subtotal, coins_to_redeem) =>
    api.post('/coins/redeem-preview', { subtotal, coins_to_redeem }).then(r => r.data),
  scratch: (orderId) => api.post(`/coins/scratch/${orderId}`).then(r => r.data),
};

export const referrals = {
  me: () => api.get('/referrals/me').then(r => r.data),
};

export const adminPayments = {
  list: (params) => api.get('/admin/payments', { params }).then(r => r.data),
  summary: (days = 30) => api.get('/admin/payments/summary', { params: { days } }).then(r => r.data),
  get: (id) => api.get(`/admin/payments/${id}`).then(r => r.data),
  create: (data) => api.post('/admin/payments', data).then(r => r.data),
  update: (id, data) => api.patch(`/admin/payments/${id}`, data).then(r => r.data),
  orderLedger: (orderId) => api.get(`/admin/payments/order/${orderId}/ledger`).then(r => r.data),
  exportUrl: () => `${API_BASE}/admin/payments/export.csv`,
};

export const payments = {
  stripeCheckout: (orderId) => api.post('/payments/stripe/checkout', {
    order_id: orderId, origin_url: window.location.origin
  }).then(r => r.data),
  stripeStatus: (sessionId) => api.get(`/payments/stripe/status/${sessionId}`).then(r => r.data),
  razorpayOrder: (orderId) => api.post('/payments/razorpay/order', {
    order_id: orderId, origin_url: window.location.origin
  }).then(r => r.data),
  razorpayVerify: (data) => api.post('/payments/razorpay/verify', data).then(r => r.data),
  methods: () => api.get('/payments/methods').then(r => r.data),
  upiIntent: (orderId, amount, note) => api.post('/payments/upi/intent', { order_id: orderId, amount, note }).then(r => r.data),
  markUpiPaid: (intentId) => api.post(`/payments/upi/intent/${intentId}/mark-paid`).then(r => r.data),
  emiPlans: (amount) => api.get('/payments/emi-plans', { params: { amount } }).then(r => r.data),
  paypalCreate: (orderId) => api.post('/payments/paypal/create', { order_id: orderId, origin_url: window.location.origin }).then(r => r.data),
  paypalCapture: (paypalOrderId) => api.post('/payments/paypal/capture', { paypal_order_id: paypalOrderId }).then(r => r.data),
};

export const round2 = {
  domainStatus: () => api.get('/site/domain-status').then(r => r.data),
  marketPrices: (commodity, state) => api.get('/market-prices', { params: { commodity, ...(state ? { state } : {}) } }).then(r => r.data),
  storeScore: (storeId) => api.get(`/scorecard/store/${storeId}`).then(r => r.data),
  ownerScorecard: () => api.get('/owner/scorecard').then(r => r.data),
  adminScorecards: () => api.get('/admin/scorecards').then(r => r.data),
  sendWhatsApp: (phone, message) => api.post('/messaging/whatsapp/send', { phone, message }).then(r => r.data),
};

// Sends via the real WhatsApp service when connected, otherwise opens WhatsApp on this phone.
export async function deliverWhatsApp({ phone, message, wa_url }) {
  try {
    const r = await round2.sendWhatsApp(phone, message);
    if (r.sent) return 'sent';
  } catch { /* fall back to link */ }
  window.open(wa_url, '_blank', 'noopener');
  return 'opened';
}

export const tenders = {
  create: (data) => api.post('/tenders', data).then(r => r.data),
  mine: () => api.get('/tenders/me').then(r => r.data),
  get: (id) => api.get(`/tenders/${id}`).then(r => r.data),
  forOwner: () => api.get('/owner/tenders').then(r => r.data),
  bid: (tenderId, data) => api.post(`/tenders/${tenderId}/bids`, data).then(r => r.data),
  withdrawBid: (tenderId) => api.delete(`/tenders/${tenderId}/bids/me`).then(r => r.data),
  accept: (tenderId, data) => api.post(`/tenders/${tenderId}/accept`, data).then(r => r.data),
};

export const reviews = {
  add: (storeId, data) => api.post(`/stores/${storeId}/reviews`, data).then(r => r.data),
  list: (storeId) => api.get(`/stores/${storeId}/reviews`).then(r => r.data),
};

export const notifyStock = (productId, contact) =>
  api.post(`/products/${productId}/notify`, { contact }).then(r => r.data);

export const updateStore = (storeId, data) =>
  api.patch(`/stores/${storeId}`, data).then(r => r.data);

export const voice = {
  transcribe: (formData) =>
    api.post('/voice/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  order: (formData) =>
    api.post('/voice/order', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
};

export const ownerKyc = {
  get: () => api.get('/owner/kyc').then(r => r.data),
  put: (data) => api.put('/owner/kyc', data).then(r => r.data),
};

export const storePhotos = {
  updateLocation: (storeId, data) =>
    api.put(`/owner/stores/${storeId}/location`, data).then(r => r.data),
  updatePhotos: (storeId, photos) =>
    api.put(`/owner/stores/${storeId}/photos`, { photos }).then(r => r.data),
};

export const khata = {
  add: (data) => api.post('/owner/khata/entries', data).then(r => r.data),
  list: (customerPhone) =>
    api.get('/owner/khata/entries', { params: customerPhone ? { customer_phone: customerPhone } : {} }).then(r => r.data),
  customers: () => api.get('/owner/khata/customers').then(r => r.data),
  remove: (entryId) => api.delete(`/owner/khata/entries/${entryId}`).then(r => r.data),
};

// ---------- Digital Khata (Udhaar) ----------
export const digitalKhata = {
  dashboard: (params = {}) => api.get('/owner/khata/dashboard', { params }).then(r => r.data),
  customerDetail: (storeId, phone) => api.get(`/owner/khata/customer/${storeId}/${phone}`).then(r => r.data),
  trust: (data) => api.post('/owner/khata/trust', data).then(r => r.data),
  untrust: (storeId, phone) => api.delete(`/owner/khata/trust/${storeId}/${phone}`).then(r => r.data),
  repay: (data) => api.post('/owner/khata/repay', data).then(r => r.data),
  manualDebit: (data) => api.post('/owner/khata/manual-debit', data).then(r => r.data),
  openingBalance: (data) => api.post('/owner/khata/opening-balance', data).then(r => r.data),
  nudge: (storeId, phone, month) => api.get(`/owner/khata/nudge/${storeId}/${phone}`, { params: month ? { month } : {} }).then(r => r.data),
  bulkNudge: (body) => api.post('/owner/khata/nudge/bulk', body).then(r => r.data),
  eligibility: (storeId) => api.get(`/owner/khata/eligibility/${storeId}`).then(r => r.data),
  // Customer-KYC (merchant uploads customer ID)
  uploadCustomerKyc: (data) => api.post('/owner/khata/customer-kyc', data).then(r => r.data),
  getCustomerKyc: (storeId, phone) => api.get(`/owner/khata/customer-kyc/${storeId}/${phone}`).then(r => r.data),
  // Auto-schedule
  getSchedule: () => api.get('/owner/khata/nudge-schedule').then(r => r.data),
  saveSchedule: (data) => api.put('/owner/khata/nudge-schedule', data).then(r => r.data),
  // Disputes
  disputeBill: (ledgerId, reason) => api.post(`/owner/khata/ledger/${ledgerId}/dispute`, null, { params: { reason } }).then(r => r.data),
  myDisputes: () => api.get('/owner/khata/disputes/mine').then(r => r.data),
  // Ratings
  rateStore: (storeId, stars, comment) => api.post(`/owner/stores/${storeId}/honesty-rating`, { store_id: storeId, stars, comment }).then(r => r.data),
  getStoreRating: (storeId) => api.get(`/owner/stores/${storeId}/honesty-rating`).then(r => r.data),
};

export const business = {
  revenue: () => api.get('/owner/business/revenue').then(r => r.data),
};

export const terms = {
  platform: () => api.get('/terms/platform').then(r => r.data),
  acceptPlatform: () => api.post('/terms/platform/accept').then(r => r.data),
};

export const ops = {
  dashboard: () => api.get('/admin/ops/dashboard').then(r => r.data),
};

export const sysupdates = {
  list: (params) => api.get('/sysupdates/admin', { params }).then(r => r.data),
  unread: () => api.get('/sysupdates/admin/unread').then(r => r.data),
  markRead: (id) => api.post(`/sysupdates/admin/${id}/read`).then(r => r.data),
};

export const homepage = {
  categoryRails: (categories) =>
    api.get('/homepage/category-rails', { params: categories ? { categories: categories.join(',') } : {} }).then(r => r.data),
};

export const lms = {
  shipments: (params) => api.get('/lms/admin/shipments', { params }).then(r => r.data),
  shipment: (id) => api.get(`/lms/admin/shipments/${id}`).then(r => r.data),
  agents: (params) => api.get('/lms/admin/agents', { params }).then(r => r.data),
  hubs: () => api.get('/lms/admin/hubs').then(r => r.data),
  assign: (orderId, body) => api.post(`/lms/admin/shipments/${orderId}/assign`, body || {}).then(r => r.data),
  updateStatus: (shipmentId, body) => api.patch(`/lms/admin/shipments/${shipmentId}/status`, body).then(r => r.data),
  sla: () => api.get('/lms/admin/sla').then(r => r.data),
  codSummary: () => api.get('/lms/admin/cod/summary').then(r => r.data),
};

export const qms = {
  stores: (params) => api.get('/qms/admin/stores', { params }).then(r => r.data),
  products: (params) => api.get('/qms/admin/products', { params }).then(r => r.data),
  employees: (params) => api.get('/qms/admin/employees', { params }).then(r => r.data),
  agents: (params) => api.get('/qms/admin/delivery-agents', { params }).then(r => r.data),
  scorecard: (type, id) => api.get(`/qms/admin/scorecard/${type}/${id}`).then(r => r.data),
  recomputeAll: () => api.post('/qms/admin/recompute').then(r => r.data),
  recompute: (type, id) => api.post(`/qms/admin/recompute/${type}/${id}`).then(r => r.data),
};

export const dps = {
  dsar: (params) => api.get('/dps/admin/dsar', { params }).then(r => r.data),
  updateDsar: (id, body) => api.patch(`/dps/admin/dsar/${id}`, body).then(r => r.data),
  grievances: (params) => api.get('/dps/admin/grievances', { params }).then(r => r.data),
  updateGrievance: (id, body) => api.patch(`/dps/admin/grievances/${id}`, body).then(r => r.data),
  breach: (params) => api.get('/dps/admin/breach', { params }).then(r => r.data),
  sosList: () => api.get('/dps/safety/sos/admin').then(r => r.data),
};

export const standards = {
  list: (params) => api.get('/compliance/standards', { params }).then(r => r.data),
  add: (body) => api.post('/compliance/admin/standards', body).then(r => r.data),
  patch: (code, body) => api.patch(`/compliance/admin/standards/${code}`, body).then(r => r.data),
  reviewQueue: () => api.get('/compliance/admin/review-queue').then(r => r.data),
  health: () => api.get('/compliance/admin/health').then(r => r.data),
};

export const auditApi = {
  business: (params) => api.get('/audit/business', { params }).then(r => r.data),
  health: (days = 90) => api.get('/audit/business/summary/health', { params: { days } }).then(r => r.data),
  software: (params) => api.get('/audit/software', { params }).then(r => r.data),
  softwareSummary: (days = 7) => api.get('/audit/software/summary', { params: { days } }).then(r => r.data),
};

export const priceAlerts = {
  create: (body) => api.post('/price-alerts', body).then(r => r.data),
  mine: () => api.get('/price-alerts/me').then(r => r.data),
  cancel: (id) => api.delete(`/price-alerts/${id}`).then(r => r.data),
};

export const orderLedger = {
  list: (params) => api.get('/admin/order-ledger', { params }).then(r => r.data),
  summary: (days = 30) => api.get('/admin/order-ledger/summary', { params: { days } }).then(r => r.data),
  one: (order_id) => api.get(`/admin/order-ledger/${order_id}`).then(r => r.data),
  ownerMine: (params) => api.get('/owner/order-ledger', { params }).then(r => r.data),
  exportUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API_BASE}/admin/order-ledger/export.csv${qs ? `?${qs}` : ''}`;
  },
  markPaid: (body) => api.post('/admin/order-ledger/mark-paid', body).then(r => r.data),
};

export const payouts = {
  summary: (days = 90) => api.get('/owner/payouts/summary', { params: { days } }).then(r => r.data),
  orders: (params) => api.get('/owner/payouts/orders', { params }).then(r => r.data),
  history: (limit = 20) => api.get('/owner/payouts/history', { params: { limit } }).then(r => r.data),
  identity: () => api.get('/platform/identity').then(r => r.data),
  statementUrl: () => `${API_BASE}/owner/payouts/statement.csv`,
};

export const gstr8 = {
  summary: (month) => api.get('/admin/gstr8/summary', { params: month ? { month } : {} }).then(r => r.data),
  exportUrl: (month) => `${API_BASE}/admin/gstr8/export.csv${month ? `?month=${month}` : ''}`,
};

export const masterCatalog = {
  list: (shopType) => api.get('/master-catalog', { params: shopType ? { shop_type: shopType } : {} }).then(r => r.data),
  addToShop: (data) => api.post('/owner/master-catalog/add-to-shop', data).then(r => r.data),
  statusForShop: (storeId) => api.get(`/owner/master-catalog/status/${storeId}`).then(r => r.data),
  adminOverride: (id, data) => api.patch(`/admin/master-catalog/${id}`, data).then(r => r.data),
  shopTypeAnalytics: () => api.get('/admin/analytics/shop-types').then(r => r.data),
};

export const accountantPack = {
  downloadUrl: (month) => `${API_BASE}/admin/accountant-pack.zip${month ? `?month=${month}` : ''}`,
  emailToCa: (body) => api.post('/admin/accountant-pack/email', body).then(r => r.data),
  getCaSettings: () => api.get('/admin/settings/ca').then(r => r.data),
  saveCaSettings: (body) => api.put('/admin/settings/ca', body).then(r => r.data),
};

// ---------- Analytics / Compliance Heatmap ----------
export const analytics = {
  users: (q) => api.get('/admin/activity/users', { params: q ? { q } : {} }).then(r => r.data),
  daily: (days = 14) => api.get('/admin/activity/daily', { params: { days } }).then(r => r.data),
  logins: (limit = 100) => api.get('/admin/activity/logins', { params: { limit } }).then(r => r.data),
  logVisit: (body, visitorId) =>
    api.post('/activity/visit', body, {
      headers: visitorId ? { 'X-Visitor-Id': visitorId } : {},
    }).then(r => r.data),
  csvUrl: (what = 'users') => `${API_BASE}/admin/activity/export.csv?what=${encodeURIComponent(what)}`,
  heatmap: () => api.get('/admin/compliance/heatmap').then(r => r.data),
};

// ---------- Legal & Compliance Audit ----------
export const compliance = {
  getTos: () => api.get('/compliance/merchant-tos').then(r => r.data),
  signTos: (body) => api.post('/compliance/merchant-tos/sign', body).then(r => r.data),
  tosStatus: () => api.get('/compliance/merchant-tos/status').then(r => r.data),
  getVault: (storeId) => api.get(`/compliance/stores/${storeId}/vault`).then(r => r.data),
  saveVault: (storeId, body) => api.put(`/compliance/stores/${storeId}/vault`, body).then(r => r.data),
  confirmMedicalOrder: (body) => api.post('/compliance/orders/confirm-medical', body).then(r => r.data),
  generateEvidencePdf: (orderId) => api.post(`/compliance/orders/${orderId}/evidence-pdf`).then(r => r.data),
  getEvidencePdf: (orderId) => api.get(`/compliance/orders/${orderId}/evidence-pdf`).then(r => r.data),
  setBatchExpiry: (orderId, body) => api.post(`/compliance/orders/${orderId}/batch-expiry`, body).then(r => r.data),
};

// ---------- Voice Message Orders ----------
export const voiceOrders = {
  create: (formData) => api.post('/voice-orders', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  mine: () => api.get('/voice-orders/mine').then(r => r.data),
  ownerList: (params) => api.get('/owner/voice-orders', { params }).then(r => r.data),
  updateStatus: (id, body) => api.post(`/voice-orders/${id}/status`, body).then(r => r.data),
  get: (id) => api.get(`/voice-orders/${id}`).then(r => r.data),
};

// ---------- Emergency SOS ----------
export const emergencySos = {
  broadcast: (body) => api.post('/emergency/broadcast', body).then(r => r.data),
  mine: () => api.get('/emergency/mine').then(r => r.data),
  get: (id) => api.get(`/emergency/${id}`).then(r => r.data),
  respond: (id, body) => api.post(`/emergency/${id}/respond`, body).then(r => r.data),
  close: (id) => api.post(`/emergency/${id}/close`).then(r => r.data),
  ownerAlerts: (status) => api.get('/owner/emergency-alerts', { params: status ? { status } : {} }).then(r => r.data),
};

// ---------- Community Trust & Safety ----------
export const communityTrust = {
  vouch: (storeId, note) => api.post(`/stores/${storeId}/vouch`, { note }).then(r => r.data),
  unvouch: (storeId) => api.delete(`/stores/${storeId}/vouch`).then(r => r.data),
  vouches: (storeId) => api.get(`/stores/${storeId}/vouches`).then(r => r.data),
  myStatus: (storeId) => api.get(`/stores/${storeId}/my-vouch`).then(r => r.data),
  report: (storeId, body) => api.post(`/stores/${storeId}/report`, body).then(r => r.data),
  reportsSummary: (storeId) => api.get(`/stores/${storeId}/reports/summary`).then(r => r.data),
  uploadLicensePhoto: (storeId, body) =>
    api.put(`/owner/stores/${storeId}/license-photo`, body).then(r => r.data),
  adminList: (params) => api.get('/admin/store-reports', { params }).then(r => r.data),
  adminResolve: (reportId, body) => api.post(`/admin/store-reports/${reportId}/resolve`, body).then(r => r.data),
  adminUnsuspend: (storeId) => api.post(`/admin/stores/${storeId}/unsuspend`).then(r => r.data),
};

// ---------- Supply Chain — Network entities (MS-IDs) ----------
export const network = {
  mine: () => api.get('/network/mine').then(r => r.data),
  register: (body) => api.post('/network/entities/register', body).then(r => r.data),
  lookup: (ms_id) => api.get(`/network/entities/lookup/${ms_id}`).then(r => r.data),
  linkParent: (ms_id, parent_ms_id) =>
    api.post(`/network/entities/${ms_id}/link-parent`, { ms_id: parent_ms_id }).then(r => r.data),
  tree: (ms_id) => api.get(`/network/entities/${ms_id}/tree`).then(r => r.data),
  addProduct: (body) => api.post('/network/products', body).then(r => r.data),
  factoryCatalog: (ms_id) => api.get(`/network/factory/${ms_id}/catalog`).then(r => r.data),
};

// ---------- Supply Chain — Purchase Orders (ORD-MS-*) ----------
export const purchaseOrders = {
  create: (body) => api.post('/po/create', body).then(r => r.data),
  mine: (status) => api.get('/po/mine', { params: status ? { status } : {} }).then(r => r.data),
  get: (id) => api.get(`/po/${id}`).then(r => r.data),
  updateStatus: (id, body) => api.post(`/po/${id}/status`, body).then(r => r.data),
  scanReceive: (id, coords) =>
    api.post(`/po/${id}/scan-receive`, null, { params: coords || {} }).then(r => r.data),
  submitPayment: (id, formData) =>
    api.post(`/po/${id}/payment/submit`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data),
  verifyPayment: (id, body) => api.post(`/po/${id}/payment/verify`, body).then(r => r.data),
  getPayment: (id) => api.get(`/po/${id}/payment`).then(r => r.data),
};

// ---------- 360° Reputation Engine ----------
export const reputation = {
  // Public reviews (product / store / factory / agent)
  create: (formData) =>
    api.post('/reviews', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(r => r.data),
  forTarget: (kind, targetId, limit = 100) =>
    api.get(`/reviews/target/${kind}/${targetId}`, { params: { limit } }).then(r => r.data),
  edit: (id, body) => api.patch(`/reviews/${id}`, body).then(r => r.data),
  remove: (id) => api.delete(`/reviews/${id}`).then(r => r.data),
  mySummary: () => api.get('/reviews/me/summary').then(r => r.data),

  // Private — merchant → customer credit
  rateCustomer: (body) => api.post('/reviews/customer', body).then(r => r.data),
  customerRatings: (phone) => api.get(`/reviews/customer/${phone}`).then(r => r.data),

  // Admin
  lowPerformance: (min_reviews = 3) =>
    api.get('/admin/reviews/low-performance', { params: { min_reviews } }).then(r => r.data),

  // Merchant response
  respond: (reviewId, text) =>
    api.post(`/reviews/${reviewId}/response`, { text }).then(r => r.data),
  deleteResponse: (reviewId) =>
    api.delete(`/reviews/${reviewId}/response`).then(r => r.data),

  // Review reminders — nudges 24h post-delivery / 1h post-Khata-payoff
  myReminders: () => api.get('/reviews/reminders/mine').then(r => r.data),
  dismissReminder: (id) => api.post(`/reviews/reminders/${id}/dismiss`).then(r => r.data),
};

// ---------- Hyper-local Delivery Fleet ----------
export const fleet = {
  register: (formData) =>
    api.post('/fleet/register', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data),
  me: () => api.get('/fleet/me').then(r => r.data),
  updateLocation: (body) => api.post('/fleet/location', body).then(r => r.data),

  // PDF templates for signing
  bondTemplateUrl: (voucher_ms_id) =>
    `${(api.defaults.baseURL || '')}/fleet/documents/bond-template${voucher_ms_id ? '?voucher_ms_id=' + voucher_ms_id : ''}`,
  voucherTemplateUrl: (voucher_ms_id) =>
    `${(api.defaults.baseURL || '')}/fleet/documents/voucher-template${voucher_ms_id ? '?voucher_ms_id=' + voucher_ms_id : ''}`,
  downloadBondTemplate: (voucher_ms_id) =>
    api.get('/fleet/documents/bond-template', { responseType: 'blob',
      params: voucher_ms_id ? { voucher_ms_id } : {} }),
  downloadVoucherTemplate: (voucher_ms_id) =>
    api.get('/fleet/documents/voucher-template', { responseType: 'blob',
      params: voucher_ms_id ? { voucher_ms_id } : {} }),

  // Merchant → dispatch board
  dispatchReady: (orderId, body) =>
    api.post(`/orders/${orderId}/dispatch-ready`, body || {}).then(r => r.data),
  orderDispatch: (orderId) => api.get(`/orders/${orderId}/dispatch`).then(r => r.data),

  // Partner
  availableTasks: (radius_km = 5) =>
    api.get('/fleet/tasks/available', { params: { radius_km } }).then(r => r.data),
  myTasks: (status) =>
    api.get('/fleet/tasks/mine', { params: status ? { status } : {} }).then(r => r.data),
  accept: (taskId) => api.post(`/fleet/tasks/${taskId}/accept`).then(r => r.data),
  pickup: (taskId, formData) =>
    api.post(`/fleet/tasks/${taskId}/pickup`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data),
  deliveryPhoto: (taskId, formData) =>
    api.post(`/fleet/tasks/${taskId}/delivery-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data),
  complete: (taskId, body) =>
    api.post(`/fleet/tasks/${taskId}/complete`, body).then(r => r.data),
  earnings: () => api.get('/fleet/earnings').then(r => r.data),

  // Wallet + COD
  wallet: () => api.get('/fleet/wallet').then(r => r.data),
  codPending: () => api.get('/fleet/cod/pending').then(r => r.data),
  codDeposit: (body) => api.post('/fleet/cod/deposit', body).then(r => r.data),

  // Anti-fraud
  reportMischief: (body) => api.post('/fleet/mischief', body).then(r => r.data),
  adminMischief: () => api.get('/admin/fleet/mischief').then(r => r.data),
  adminUnsuspend: (partnerId) =>
    api.post(`/admin/fleet/${partnerId}/unsuspend`).then(r => r.data),
  adminFreeze: (partnerId, reason) =>
    api.post(`/admin/fleet/${partnerId}/freeze`, null, { params: reason ? { reason } : {} }).then(r => r.data),
  adminUnfreeze: (partnerId) =>
    api.post(`/admin/fleet/${partnerId}/unfreeze`).then(r => r.data),
  adminListPartners: (status) =>
    api.get('/admin/fleet/partners', { params: status ? { status } : {} }).then(r => r.data),
  adminDriverDocs: (driverId) =>
    api.get(`/admin/fleet/${driverId}/documents`).then(r => r.data),
};

// ---------- AI Purchase Agent (10-engine procurement network) ----------
export const aiPurchase = {
  search: (body) => api.post('/ai-purchase/search', body).then(r => r.data),
  explain: (option, spec) => api.post('/ai-purchase/explain', { option, spec }).then(r => r.data),
  negotiate: (body) => api.post('/ai-purchase/negotiate', body).then(r => r.data),
  negotiateRespond: (id, body) => api.post(`/ai-purchase/negotiate/${id}/respond`, body).then(r => r.data),
  negotiations: () => api.get('/ai-purchase/negotiations').then(r => r.data),
  approve: (body) => api.post('/ai-purchase/approve', body).then(r => r.data),
  getAutoLimits: () => api.get('/ai-purchase/auto-limits').then(r => r.data),
  putAutoLimits: (body) => api.put('/ai-purchase/auto-limits', body).then(r => r.data),
  autoBuy: (body) => api.post('/ai-purchase/auto-buy', body).then(r => r.data),
  feedback: (body) => api.post('/ai-purchase/feedback', body).then(r => r.data),
  insights: () => api.get('/ai-purchase/insights').then(r => r.data),
};

// ---------- Business Day Book + Complaint Management ----------
export const biz = {
  me: () => api.get('/business/me').then(r => r.data),
  // Activity types
  listActivityTypes: () => api.get('/business/activity-types').then(r => r.data),
  addActivityType: (name) => api.post('/business/activity-types', { name }).then(r => r.data),
  delActivityType: (id) => api.delete(`/business/activity-types/${id}`).then(r => r.data),
  // Day Book
  listDaybook: (params) => api.get('/business/daybook', { params }).then(r => r.data),
  getDaybook: (id) => api.get(`/business/daybook/${id}`).then(r => r.data),
  createDaybook: (body) => api.post('/business/daybook', body).then(r => r.data),
  patchDaybook: (id, body) => api.patch(`/business/daybook/${id}`, body).then(r => r.data),
  delDaybook: (id) => api.delete(`/business/daybook/${id}`).then(r => r.data),
  exportDaybookCsvUrl: (params = {}) => {
    const url = new URL(`${API_BASE}/business/daybook/export.csv`);
    Object.entries(params).forEach(([k, v]) => v && url.searchParams.append(k, v));
    return url.toString();
  },
  // Customers
  listCustomers: (params) => api.get('/business/customers', { params }).then(r => r.data),
  getCustomer: (id) => api.get(`/business/customers/${id}`).then(r => r.data),
  customerHistory: (id) => api.get(`/business/customers/${id}/history`).then(r => r.data),
  createCustomer: (body) => api.post('/business/customers', body).then(r => r.data),
  patchCustomer: (id, body) => api.patch(`/business/customers/${id}`, body).then(r => r.data),
  // Complaints
  listComplaints: (params) => api.get('/business/complaints', { params }).then(r => r.data),
  getComplaint: (no) => api.get(`/business/complaints/${no}`).then(r => r.data),
  createComplaint: (body) => api.post('/business/complaints', body).then(r => r.data),
  changeStatus: (no, body) => api.post(`/business/complaints/${no}/status`, body).then(r => r.data),
  addTimeline: (no, body) => api.post(`/business/complaints/${no}/timeline`, body).then(r => r.data),
  addComm: (no, body) => api.post(`/business/complaints/${no}/comm`, body).then(r => r.data),
  // Templates
  listTemplates: () => api.get('/business/templates').then(r => r.data),
  upsertTemplate: (key, body) => api.put(`/business/templates/${key}`, body).then(r => r.data),
  // SLA
  listSlaRules: () => api.get('/business/sla-rules').then(r => r.data),
  upsertSlaRule: (body) => api.post('/business/sla-rules', body).then(r => r.data),
  // Follow-ups
  listFollowups: (params) => api.get('/business/followups', { params }).then(r => r.data),
  createFollowup: (body) => api.post('/business/followups', body).then(r => r.data),
  completeFollowup: (id, remarks) => api.post(`/business/followups/${id}/complete`, { remarks }).then(r => r.data),
  // Day closing
  dayClose: (day, remarks) => api.post('/business/day-close', { day, remarks }).then(r => r.data),
  dayReopen: (day) => api.post(`/business/day-close/${day}/reopen`).then(r => r.data),
  listDayClosings: () => api.get('/business/day-close').then(r => r.data),
  daySummary: (day) => api.get('/business/day-close/summary', { params: { day } }).then(r => r.data),
  // Dashboard
  dashboardToday: () => api.get('/business/dashboard/today').then(r => r.data),
  complaintKpis: () => api.get('/business/dashboard/complaint-kpis').then(r => r.data),
  // Global search & audit
  search: (q) => api.get('/business/search', { params: { q } }).then(r => r.data),
  auditLog: (params) => api.get('/business/audit-log', { params }).then(r => r.data),
  // Users / roles
  listBizUsers: () => api.get('/business/users').then(r => r.data),
  setBizRole: (uid, business_role) => api.put(`/business/users/${uid}/role`, { business_role }).then(r => r.data),
};

// ---------- Village UX: Quick Launch, Voice → Cart, My Khata ----------
export const villageUx = {
  quickLaunch: (storeId) => api.get(`/owner/quick-launch/${storeId}`).then(r => r.data),
  toggle: (storeId, key, on) => api.post(`/owner/quick-launch/${storeId}/toggle`, { key, on }).then(r => r.data),
  setPrice: (storeId, key, price) => api.patch(`/owner/quick-launch/${storeId}/price`, { key, price }).then(r => r.data),
  voiceCart: (formData) => api.post('/voice/cart', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  textCart: (text) => api.post('/voice/cart-text', { text }).then(r => r.data),
  myKhata: () => api.get('/khata/mine').then(r => r.data),
  speak: (text) => api.post('/voice/speak', { text }, { responseType: 'blob' }).then(r => r.data),
  tutorial: () => api.get('/settings/merchant-tutorial').then(r => r.data),
  setTutorial: (url) => api.put('/admin/settings/merchant-tutorial', { url }).then(r => r.data),
};

// ---------- Company profile & tax statements ----------
export const tax = {
  company: () => api.get('/admin/company-profile').then(r => r.data),
  saveCompany: (body) => api.put('/admin/company-profile', body).then(r => r.data),
  mine: (month) => api.get('/owner/tax-statement', { params: { month } }).then(r => r.data),
  mineCsv: (month) => api.get('/owner/tax-statement.csv', { params: { month }, responseType: 'blob' }).then(r => r.data),
  admin: (month) => api.get('/admin/tax-statements', { params: { month } }).then(r => r.data),
  adminCsv: (month) => api.get('/admin/tax-statements.csv', { params: { month }, responseType: 'blob' }).then(r => r.data),
};

// ---------- Digital signatures (PO) ----------
export const signatures = {
  me: () => api.get('/signature/me').then(r => r.data),
  save: (body) => api.put('/signature/me', body).then(r => r.data),
  passkeyRegisterOptions: () => api.post('/passkeys/register/options').then(r => r.data),
  passkeyRegisterVerify: (credential) => api.post('/passkeys/register/verify', { credential }).then(r => r.data),
  poPasskeyOptions: (poId, role) => api.post(`/po/${poId}/sign/passkey-options`, null, { params: { role } }).then(r => r.data),
  signPo: (poId, body) => api.post(`/po/${poId}/sign`, body).then(r => r.data),
  verify: (hash) => api.get(`/po/signature/verify/${hash}`).then(r => r.data),
  share: (poId) => api.post(`/po/${poId}/share`).then(r => r.data),
  shared: (token) => api.get(`/po/share/${token}`).then(r => r.data),
  fileBlob: (url) => api.get(url.replace(/^\/api/, ''), { responseType: 'blob' }).then(r => r.data),
};

export const market = {
  meta: () => api.get('/marketplace/meta').then(r => r.data),
  uploadDoc: (file, kind) => {
    const fd = new FormData(); fd.append('file', file); fd.append('kind', kind);
    return api.post('/partners/documents', fd).then(r => r.data);
  },
  apply: (body) => api.post('/partners/applications', body).then(r => r.data),
  myApplications: () => api.get('/partners/applications/me').then(r => r.data),
  adminApplications: (params) => api.get('/admin/partners/applications', { params }).then(r => r.data),
  adminApplication: (id) => api.get(`/admin/partners/applications/${id}`).then(r => r.data),
  adminDecide: (id, decision, note) => api.post(`/admin/partners/applications/${id}/decision`, { decision, note }).then(r => r.data),
  auditLog: () => api.get('/admin/audit-log').then(r => r.data),
  brands: (params) => api.get('/brands', { params }).then(r => r.data),
  brand: (slug, params) => api.get(`/brands/${slug}`, { params }).then(r => r.data),
  myBrands: () => api.get('/partners/my-brands').then(r => r.data),
  updateBrand: (id, body) => api.patch(`/partners/brands/${id}`, body).then(r => r.data),
  manufacturers: (params) => api.get('/manufacturers', { params }).then(r => r.data),
  manufacturer: (slug) => api.get(`/manufacturers/${slug}`).then(r => r.data),
  search: (params) => api.get('/catalog/products', { params }).then(r => r.data),
  product: (slug) => api.get(`/catalog/products/${slug}`).then(r => r.data),
  match: (params) => api.get('/catalog/match', { params }).then(r => r.data),
  createListing: (body) => api.post('/catalog/products', body).then(r => r.data),
  addOffer: (cpId, body) => api.post(`/catalog/products/${cpId}/offers`, body).then(r => r.data),
  myOffers: () => api.get('/catalog/my-offers').then(r => r.data),
  adminCatalog: (status) => api.get('/admin/catalog/products', { params: { status } }).then(r => r.data),
  adminCatalogDecide: (id, decision, note) => api.post(`/admin/catalog/products/${id}/decision`, { decision, note }).then(r => r.data),
};

export const PARTNER_LABELS = { manufacturer: 'Manufacturer', brand_owner: 'Brand Owner', distributor: 'Authorised Distributor', retailer: 'Retail Seller' };
export const openPrivateFile = async (url) => {
  const r = await api.get(url.replace(/^\/api/, ''), { responseType: 'blob' });
  window.open(URL.createObjectURL(r.data), '_blank', 'noopener');
};

export const bc = {
  home: () => api.get('/home/national').then(r => r.data),
  wishlist: () => api.get('/wishlist').then(r => r.data),
  wishAdd: (id) => api.post(`/wishlist/${id}`).then(r => r.data),
  wishRemove: (id) => api.delete(`/wishlist/${id}`).then(r => r.data),
  checkout: (body) => api.post('/checkout/national', body).then(r => r.data),
  verifyGroup: (groupId, body) => api.post(`/checkout/national/${groupId}/razorpay/verify`, body).then(r => r.data),
  group: (groupId) => api.get(`/checkout/national/${groupId}`).then(r => r.data),
  sellerOrders: () => api.get('/seller/national-orders').then(r => r.data),
  ship: (orderId, body) => api.patch(`/orders/${orderId}/shipment`, body).then(r => r.data),
  statement: () => api.get('/seller/bc-statement').then(r => r.data),
  rates: () => api.get('/admin/bc-commission').then(r => r.data),
  saveRates: (body) => api.put('/admin/bc-commission', body).then(r => r.data),
  linkRoute: (userId, accountId) => api.post(`/admin/sellers/${userId}/razorpay-account`, { account_id: accountId }).then(r => r.data),
  emailSend: () => api.post('/auth/email/verify/send').then(r => r.data),
  emailVerify: (code) => api.post('/auth/email/verify', { code }).then(r => r.data),
};
